<template>
    <div class="knowledge-graph">
        <el-button
            class="fullscreen-button"
            size="mini"
            icon="el-icon-full-screen"
            aria-label="全屏展示知识图谱"
            :disabled="!canDisplay"
            @click="openFullscreen"
        >全屏展示</el-button>
        <disease-chart v-if="canDisplay" :option="graphOption" :height="height" />
        <div v-else class="graph-state" :style="{ height: stateHeight }" role="status" aria-live="polite">
            <span v-if="requestLoading"><i class="el-icon-loading"></i> 图谱加载中...</span>
            <el-empty v-else-if="requestError" description="加载失败，请重试" :image-size="100">
                <el-button type="primary" size="small" @click="retry">重新加载</el-button>
            </el-empty>
            <el-empty v-else class="graph-empty" description="暂无相关图谱数据" :image-size="40" />
        </div>

        <el-dialog
            :title="title"
            :visible.sync="fullscreen"
            fullscreen
            append-to-body
            destroy-on-close
            :show-close="false"
            custom-class="disease-graph-fullscreen"
            @opened="resizeFullscreenChart"
        >
            <div slot="title" class="fullscreen-header">
                <span>{{ title }}</span>
                <el-button size="small" icon="el-icon-close" @click="fullscreen = false">退出全屏</el-button>
            </div>
            <disease-chart
                v-if="fullscreen && canDisplay"
                ref="fullscreenChart"
                :option="graphOption"
                height="100%"
            />
        </el-dialog>
    </div>
</template>

<script>
import DiseaseChart from './disease_chart.vue';
import { buildGraphOption } from './buildGraphOption';

export default {
    name: 'KnowledgeGraph',
    components: { DiseaseChart },
    // 请求由模块统一发起；跨越三元组等包装组件共享加载/失败状态。
    inject: { diseaseGraphRequest: { default: null } },
    props: {
        // 所有模块统一接收科研接口格式：{ nodes: [{ id, label, degree, distance }],
        // edges: [{ source, target, label, value }] }，不直接传入 ECharts nodes/links。
        graphData: {
            type: Object,
            default: () => ({ nodes: [], edges: [] }),
        },
        title: { type: String, default: '知识图谱' },
        height: { type: String, default: '480px' },
    },
    data() {
        return { fullscreen: false };
    },
    computed: {
        stateHeight() {
            // 无数据时图谱区域固定为 100px；加载中和请求失败时保留传入高度。
            return this.requestLoading || this.requestError ? this.height : '100px';
        },
        requestLoading() {
            return !!(this.diseaseGraphRequest && this.diseaseGraphRequest.loading);
        },
        requestError() {
            return !!(this.diseaseGraphRequest && this.diseaseGraphRequest.error);
        },
        canDisplay() {
            return !this.requestLoading && !this.requestError && !!(this.graphData &&
                Array.isArray(this.graphData.nodes) && this.graphData.nodes.length);
        },
        graphOption() {
            return buildGraphOption(this.graphData);
        },
    },
    watch: {
        canDisplay(value) {
            // 切换疾病、请求失败或数据变空时关闭已打开的全屏窗口。
            if (!value) this.fullscreen = false;
        },
    },
    methods: {
        openFullscreen() {
            if (this.canDisplay) this.fullscreen = true;
        },
        retry() {
            if (this.diseaseGraphRequest) this.diseaseGraphRequest.retry();
        },
        resizeFullscreenChart() {
            // 弹窗动画结束后重新测量，确保全屏画布使用实际可用尺寸。
            if (this.$refs.fullscreenChart) this.$refs.fullscreenChart.handleResize();
        },
    },
};
</script>

<style lang="less" scoped>
.knowledge-graph {
    position: relative;

    .graph-state {
        display: flex;
        align-items: center;
        justify-content: center;
        color: #909399;
    }

    // 缩小空状态插图及留白，确保图文完整容纳在 100px 高度内。
    .graph-empty {
        padding: 0;

        ::v-deep .el-empty__description {
            margin-top: 8px;
        }
    }

    .fullscreen-button {
        position: absolute;
        top: -30px;
        right: 0;
        z-index: 1;
        color: #165DFF;
        border-color: #C6DEFF;

        &.is-disabled {
            color: #C0C4CC;
            border-color: #EBEEF5;
            background: #F5F7FA;
        }
    }
}
</style>

<!-- 全屏弹窗挂载到 body，专属类名避免影响其他弹窗。 -->
<style lang="less">
.disease-graph-fullscreen.el-dialog {
    display: flex;
    flex-direction: column;

    .el-dialog__header {
        flex: none;
        padding: 16px 20px;
        border-bottom: 1px solid #E8EEF8;
    }

    .fullscreen-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: #0C55AA;
        font-size: 16px;
        font-weight: bold;
    }

    .el-dialog__body {
        flex: 1;
        min-height: 0;
        padding: 16px 20px;
    }
}
</style>
